import { MonoidSum } from '@effect/typeclass/data/Number';
import { combineArray } from '@miles/prelude/array';
import * as Sentry from '@sentry/browser';
import { Array, Number, Option, pipe, Record } from 'effect';
import { isObject } from 'effect/Predicate';
import { match, P } from 'ts-pattern';
import { z } from 'zod';
import { AdjustmentParseService } from '../Adjustment/AdjustmentParseService';
import { unsafeConvertLegacyInsuranceLineItemToAdjustment } from '../Legacy/Insurance';
const legacyIsCanceled = z.object({
    isCanceled: z.literal(false).or(z.undefined()).optional()
});
export const legacyAttendeeItemMetadataSchema = z.object({
    type: z.undefined().optional(),
    attendeeId: z.array(z.string()).or(z.string())
});
export const transformLegacyAttendeeItemMetadataSchema = legacyAttendeeItemMetadataSchema.transform((data) => ({
    attendees: Object.fromEntries((Array.isArray(data.attendeeId)
        ? data.attendeeId
        : [data.attendeeId]).map((attendeeId) => [attendeeId, {}]))
}));
export const attendeeMetadataSchema = z.object({
    attendees: z.record(z.string(), z
        .object({
        gender: z.union([z.literal('MALE'), z.literal('FEMALE')]),
        isLicensed: z.boolean(),
        dateOfBirth: z.coerce.date(),
        groupId: z.coerce.number()
    })
        .partial()),
    PRERESERVED: z.boolean().optional()
});
const AttendeeBibMetadataSchema = attendeeMetadataSchema.or(transformLegacyAttendeeItemMetadataSchema);
const sumAdjustmentAmount = combineArray(MonoidSum)((adjustment) => adjustment.amount);
export const legacyAdjustmentAmountCalculation = (subTotal, adjustments) => pipe(adjustments, Array.partition((adjustment) => adjustment.type === 'Reduction' &&
    'metadata' in adjustment &&
    isObject(adjustment.metadata) &&
    'reductionsBreakdown' in adjustment.metadata), ([others, reductions]) => pipe(reductions, Array.reduce({
    subTotal,
    adjustedAdjustments: []
}, (acc, adj) => {
    const amount = adj.reduction.amountType === 'FIXED'
        ? adj.reduction.amount
        : (adj.reduction.amount / 100) * acc.subTotal;
    const currentAdjustment = {
        ...adj,
        amount: Math.min(amount, acc.subTotal)
    };
    return {
        ...acc,
        subTotal: Math.max(acc.subTotal - amount, 0),
        adjustedAdjustments: [
            ...acc.adjustedAdjustments,
            currentAdjustment
        ]
    };
}), ({ adjustedAdjustments }) => [...adjustedAdjustments, ...others]));
const SoloBiBLineItemSchema = (medusaLineItem) => z
    .object({
    total: z.number().optional(),
    subtotal: z.number().optional(),
    quantity: z.literal(1),
    metadata: AttendeeBibMetadataSchema.and(legacyIsCanceled).refine((data) => {
        const attendeeIds = Object.keys(data.attendees);
        return attendeeIds.length === 1 && attendeeIds[0] !== '';
    })
})
    .passthrough()
    .transform((data) => {
    var _a;
    const attendee = Record.collect(data.metadata.attendees, (id, data) => ({
        id,
        ...data,
        subtotal: 0 // Not meant to be used in a solo bib
    }))[0];
    // add attendeeId to adjustments metadata for legacy support
    const parseAdjustmentsParams = medusaLineItem.adjustments.map((adjustment) => ({
        ...adjustment,
        metadata: {
            ...adjustment.metadata,
            attendeeId: adjustment.metadata && 'attendeeId' in adjustment.metadata
                ? adjustment.metadata.attendeeId
                : attendee.id
        }
    }));
    const { adjustments: allAdjustments, insurance, autoDiscountByAttendee } = AdjustmentParseService.parseAdjustments(parseAdjustmentsParams);
    const autoDiscountTotal = (_a = autoDiscountByAttendee[attendee.id]) !== null && _a !== void 0 ? _a : 0;
    const unit_price = medusaLineItem.unit_price - autoDiscountTotal;
    const adjustments = legacyAdjustmentAmountCalculation(unit_price, allAdjustments);
    const discount_total = Math.round(sumAdjustmentAmount(adjustments));
    return {
        ...medusaLineItem,
        ...data,
        isPrereserved: data.metadata.PRERESERVED === true,
        type: 'SoloBib',
        attendee,
        adjustments,
        insurance,
        discount_total,
        subtotal: unit_price,
        unit_price,
        total: unit_price - discount_total
    };
});
const TeamBibLineItemSchema = (medusaLineItem) => z
    .object({
    total: z.number().optional(),
    subtotal: z.number().optional(),
    metadata: z
        .object({
        shareId: z.string()
    })
        .and(legacyIsCanceled)
        .and(AttendeeBibMetadataSchema)
})
    .passthrough()
    .transform((data) => {
    var _a;
    const attendees = Record.collect(data.metadata.attendees, (id, data) => ({
        id,
        ...data,
        subtotal: 0
    }));
    // add attendeeId to adjustments metadata for legacy support
    const parseAdjustmentsParams = medusaLineItem.adjustments.map((adjustment) => {
        var _a;
        return ({
            ...adjustment,
            metadata: {
                ...adjustment.metadata,
                attendeeId: adjustment.metadata && 'attendeeId' in adjustment.metadata
                    ? adjustment.metadata.attendeeId
                    : (_a = attendees[0]) === null || _a === void 0 ? void 0 : _a.id
            }
        });
    });
    const { adjustments: allAdjustments, insurance, autoDiscountByAttendee } = AdjustmentParseService.parseAdjustments(parseAdjustmentsParams);
    const autoDiscountTotal = Number.sumAll(Object.values(autoDiscountByAttendee));
    const unit_price = medusaLineItem.unit_price - autoDiscountTotal / medusaLineItem.quantity;
    const subtotal = unit_price * medusaLineItem.quantity;
    const adjustments = legacyAdjustmentAmountCalculation(unit_price, allAdjustments);
    const discount_total = Math.round(sumAdjustmentAmount(adjustments));
    for (const attendee of attendees) {
        attendee.subtotal =
            medusaLineItem.unit_price - ((_a = autoDiscountByAttendee[attendee.id]) !== null && _a !== void 0 ? _a : 0);
    }
    return {
        ...medusaLineItem,
        ...data,
        type: 'TeamBib',
        shareId: data.metadata.shareId,
        isPrereserved: data.metadata.PRERESERVED === true,
        attendees,
        insurance,
        adjustments,
        discount_total,
        subtotal,
        unit_price,
        total: subtotal - discount_total
    };
});
const FormProductLineItemSchema = (medusaLineItem) => z
    .object({
    total: z.number().optional(),
    subtotal: z.number().optional(),
    metadata: z
        .object({
        attendeeId: z.string(),
        productFormId: z.string()
    })
        .and(legacyIsCanceled)
})
    .passthrough()
    .transform((data) => {
    var _a, _b;
    const subtotal = (_a = data.subtotal) !== null && _a !== void 0 ? _a : medusaLineItem.unit_price * medusaLineItem.quantity;
    return {
        ...medusaLineItem,
        ...data,
        subtotal,
        total: (_b = data.total) !== null && _b !== void 0 ? _b : subtotal,
        type: 'FormProduct',
        attendeeId: data.metadata.attendeeId,
        productFormId: data.metadata.productFormId
    };
});
const SpecialProductLineItemSchema = (medusaLineItem) => z
    .object({
    total: z.number().optional(),
    subtotal: z.number().optional(),
    metadata: z
        .object({
        type: z.literal('COMPETITION_PASS'),
        attendeeId: z.string()
    })
        .and(legacyIsCanceled)
})
    .passthrough()
    .transform((data) => {
    var _a, _b;
    const subtotal = (_a = data.subtotal) !== null && _a !== void 0 ? _a : medusaLineItem.unit_price * medusaLineItem.quantity;
    return {
        ...medusaLineItem,
        ...data,
        subtotal,
        total: (_b = data.total) !== null && _b !== void 0 ? _b : subtotal,
        type: 'SpecialProduct',
        attendeeId: data.metadata.attendeeId
    };
});
const LineItemSchema = (medusaLineItem) => FormProductLineItemSchema(medusaLineItem)
    .or(SpecialProductLineItemSchema(medusaLineItem))
    .or(TeamBibLineItemSchema(medusaLineItem))
    .or(SoloBiBLineItemSchema(medusaLineItem));
const MedusaOrderEditLineItemMetadataType = [
    'PARTIAL_CANCELATION',
    'DISCOUNT_CANCELLATION',
    'OPTIONS_SWAP',
    'RACE_SWAP',
    'genericFees',
    'adminRefund',
    'refund'
];
const convertMedusaOrderEditLineItemMetadataTypeToOrderEditLineItemTag = (type) => ({
    PARTIAL_CANCELATION: 'PARTIAL_CANCELLATION',
    DISCOUNT_CANCELLATION: 'DISCOUNT_CANCELLATION',
    OPTIONS_SWAP: 'OPTIONS_SWAP',
    RACE_SWAP: 'RACE_SWAP',
    genericFees: 'GENERIC_FEES',
    adminRefund: 'ADMIN_REFUND',
    refund: 'REFUND'
}[type]);
const OrderLineItemSchema = (medusaLineItem) => z
    .object({
    total: z.number().optional(),
    subtotal: z.number().optional(),
    metadata: z
        .object({
        type: z.enum(MedusaOrderEditLineItemMetadataType),
        attendeeId: z.array(z.string())
    })
        .and(legacyIsCanceled)
})
    .passthrough()
    .transform((data) => {
    var _a, _b;
    const subtotal = (_a = data.subtotal) !== null && _a !== void 0 ? _a : medusaLineItem.unit_price * medusaLineItem.quantity;
    return {
        ...medusaLineItem,
        ...data,
        subtotal,
        total: (_b = data.total) !== null && _b !== void 0 ? _b : subtotal,
        type: 'OrderEdit',
        tag: convertMedusaOrderEditLineItemMetadataTypeToOrderEditLineItemTag(data.metadata.type),
        attendeeIds: data.metadata.attendeeId
    };
});
const parseOrderLineItem = (item) => LineItemSchema(item).or(OrderLineItemSchema(item)).parse(item);
const parseOrderLineItems = (items) => {
    return pipe(items, Array.filterMap((item) => match(item)
        .with({
        metadata: {
            type: P.union('cancelation', 'INVISIBLE_BALANCING')
        }
    }, () => Option.none())
        .with({
        metadata: {
            isCanceled: true
        }
    }, () => Option.none())
        .with({
        metadata: {
            type: 'PARTNER_OPTION'
        }
    }, (partnerOption) => {
        var _a;
        //Legacy insurance line items are converted to adjustments
        const bib = items.find(({ metadata }) => 
        //legacy
        ('attendeeId' in metadata &&
            typeof metadata.attendeeId === 'string' &&
            metadata.type === undefined &&
            metadata.attendeeId === partnerOption.variant.title) ||
            //legacy
            ('attendeeId' in metadata &&
                'sharedId' in metadata &&
                Array.isArray(metadata.attendeeIds) &&
                metadata.attendeeIds.includes(partnerOption.variant.title)) ||
            ('attendees' in metadata &&
                isObject(metadata.attendees) &&
                Object.keys(metadata.attendees).includes(partnerOption.variant.title)));
        if (bib) {
            const adjustment = unsafeConvertLegacyInsuranceLineItemToAdjustment(partnerOption);
            bib.adjustments = [...((_a = bib.adjustments) !== null && _a !== void 0 ? _a : []), adjustment];
        }
        return Option.none();
    })
        .otherwise(() => Option.some(item))), Array.filterMap((item) => {
        try {
            return Option.some(parseOrderLineItem(item));
        }
        catch (error) {
            console.error(error, item);
            Sentry.captureException(error);
            return Option.none();
        }
    }));
};
const parseLineItem = (item) => LineItemSchema(item).parse(item);
const parseLineItems = Array.map(parseLineItem);
export const LineItemParseService = {
    parseLineItem,
    parseLineItems,
    parseOrderLineItems
};
export const sumLineItemSubTotal = combineArray(MonoidSum)((lineItem) => lineItem.subtotal -
    ('insurance' in lineItem && !!lineItem.insurance
        ? lineItem.insurance.amount
        : 0));
export const sumLineItemDiscountTotal = combineArray(MonoidSum)((lineItem) => lineItem.type === 'SoloBib' || lineItem.type === 'TeamBib'
    ? lineItem.discount_total
    : 0);
